/**
 * **인터셉터가 던지는 오류 한 종류** — 의존성이 없는 순수 모듈이다(axios·Vue·`@/` 별칭 금지).
 *
 * `index.js` 안에 있던 클래스를 여기로 옮겼다. 이유는 하나다: **어떤 문장이 `message` 가 되는가**가 화면의
 * 갈래를 정하는 규칙인데(아래), axios 를 끌어오는 모듈 안에서는 단위 테스트가 그것에 닿을 수 없었다.
 * `index.js` 가 이 파일을 import 해 다시 export 하므로 `import { HttpError } from '@/plugins/http'` 는 그대로다.
 */

/**
 * `messages` 에 섞이는 **합성 키** — 서버가 필드 오류(`errorsMessage[]`)를 주지 않았을 때 인터셉터가
 * `ErrorResponse.error` 를 담아 만드는 자리다(`extractFieldErrors`).
 *
 * 이 값은 **HTTP 상태의 이름**이지 사용자에게 할 말이 아니다 — appserver 의 `ErrorCode` 고정 문구라
 * 400 이면 언제나 `"Bad Request Exception"` 이다. 실제 사유는 같은 응답의 `message` 에 한글 문장과 코드
 * 토큰(`TICKET_EXPIRED` 같은)으로 들어 있다.
 */
export const SYNTHETIC_ERROR_KEY = 'error'

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '')

export class HttpError extends Error {
  /**
   * @param {object} messages 필드 에러 (`add`/`up`/`patch` 가 폼에 머지한다). **그대로 보존한다** —
   *   합성 키를 포함한 이 객체의 모양이 레거시 호출부의 계약이다.
   * @param {string} fallbackMessage 서버가 내린 문장(`ErrorResponse.message`) — 한글 사유와 코드 토큰이 여기 있다
   * @param {?number} status **그 응답의 HTTP 상태** — 없으면 호출부가 갈래를 나눌 수 없다.
   *   같은 실패라도 "고쳐서 다시 보내라"(400)와 "그 사이 이미 됐다"(409)는 화면이 할 일이 다른데,
   *   이 클래스가 상태를 버리는 바람에 지금까지 어느 화면도 그것을 물을 수 없었다.
   *
   * `message` 를 고르는 순서에 규칙이 있다:
   *
   * 1. **진짜 필드 오류** — 서버가 어느 칸이 틀렸는지 말했다. 사용자에게 가장 구체적인 문장이다.
   * 2. **서버 `message`** — 필드 오류가 없으면 이것이 사유다.
   * 3. 합성 키([SYNTHETIC_ERROR_KEY])의 값 — 상태 이름뿐이지만 아무 말도 안 하는 것보다는 낫다.
   * 4. 기본 문구.
   *
   * 종전에는 1과 3을 구분하지 않고 `messages` 의 모든 값을 이어 붙여 먼저 썼다. 그래서 필드 오류가 없는
   * 400 은 **언제나** `"Bad Request Exception"` 이 `message` 가 됐고, 서버가 실어 보낸 코드 토큰이 사라져
   * 그것으로 갈래를 나누려던 화면(네이버 연동 위저드의 `TICKET_EXPIRED` 되돌리기 등)이 한 번도 발동하지
   * 못했다. 409·503 은 인터셉터가 필드 오류를 싣지 않아 우연히 동작하고 있었다.
   */
  /**
   * @param {*} data **그 응답의 본문 그대로** — 서버가 문장 말고 실어 보낸 것을 호출부가 읽는 자리다.
   *
   * 종전에는 인터셉터가 본문을 버려서, 문장으로 접히지 않는 사실(형제 매장 후보 `stores`, 엑셀 오류
   * 행 `rows`, 참여 조건 사유 `reasons`)에 화면이 닿을 수 없었다. 그래서 화면마다 우회가 생겼다 —
   * 같은 파일을 한 번 더 올려 오류 행을 되받고, 조건 사유는 아예 포기했다. 우회는 요청을 늘리거나
   * 사실을 잃는다. **버리지 않는 것**이 그 셋을 한꺼번에 없앤다.
   */
  /**
   * @param {?{method: ?string, url: ?string}} request **실패한 요청이 무엇이었나** — 인터셉터가 `failedRequestOf` 로 이미 가린
   *   방법·경로만 싣는다(요청 본문·헤더·질의 문자열 없음). 실패 로그가 어느 요청이었는지 말하게 한다(리뷰 N4).
   */
  constructor(messages = {}, fallbackMessage = '', status = null, data = null, request = null) {
    const normalizedMessages = messages || {}

    const fieldMessages = Object.keys(normalizedMessages)
      .filter((key) => key !== SYNTHETIC_ERROR_KEY)
      .map((key) => trimmed(normalizedMessages[key]))
      .filter((value) => !!value)

    const aggregated = fieldMessages.join(' | ')
    const serverMessage = trimmed(fallbackMessage)
    const synthetic = trimmed(normalizedMessages[SYNTHETIC_ERROR_KEY])

    super(aggregated || serverMessage || synthetic || '요청 처리 중 오류가 발생했습니다.')

    this.name = 'HttpError'
    this.messages = normalizedMessages
    this.status = status
    this.data = data === undefined ? null : data
    this.request = request || null
  }
}
